import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowLongRightIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, createAmountTooltip } from '../utils/currency';
import useTranslation from '../services/translationService';
import {
  fetchSportsTransactions,
  buildSportsBets,
  auditSportsSettlements,
  groupBetsByPlayer,
  getAuditFetchWindow,
  buildBetHistoryLink
} from '../utils/sportsSettlementAudit';

const shortDay = (timestamp) => (timestamp ? format(new Date(timestamp), 'MMM d') : '—');

const Money = ({ amount, currency, className = '' }) => {
  const text = formatCurrency(amount, currency);
  return (
    <span className={className} title={createAmountTooltip(amount, currency, text)}>
      {text}
    </span>
  );
};

// Renders "Jun 30 → Jul 2" with the date that sits outside the reporting
// period greyed out, so the boundary crossing is visible without reading.
const DateFlow = ({ from, to, outsideSide, t }) => {
  const inside = 'font-semibold text-gray-900 dark:text-gray-100';
  const outside = 'text-gray-400 dark:text-gray-500';

  return (
    <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
      <span className={outsideSide === 'from' ? outside : inside}>{from}</span>
      <ArrowLongRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600" />
      <span className={outsideSide === 'to' ? outside : inside}>
        {to || t('settlementAudit.stillOpen', 'open')}
      </span>
    </span>
  );
};

const BetRow = ({ bet, mode, onOpenBet, t }) => {
  const amount = mode === 'in' ? bet.payout : bet.stake;

  return (
    <button
      onClick={() => onOpenBet(bet)}
      className="group w-full flex items-center gap-2 px-3 py-3 text-left cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/10 active:bg-amber-100 dark:active:bg-amber-900/25 transition-colors"
    >
      <DateFlow
        from={bet.placementUnknown ? t('settlementAudit.earlier', 'earlier') : shortDay(bet.placedAt)}
        to={bet.stillOpen ? null : shortDay(bet.settledAt)}
        outsideSide={mode === 'in' ? 'from' : 'to'}
        t={t}
      />
      <Money
        amount={amount}
        currency={bet.currency}
        className="ml-auto text-sm font-semibold text-gray-900 dark:text-gray-100"
      />
      <span className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 group-hover:bg-amber-200 dark:group-hover:bg-amber-900/70 transition-colors">
        <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        {t('settlementAudit.viewBet', 'View')}
      </span>
    </button>
  );
};

const PlayerBlock = ({ group, currency, mode, onOpenBet, t }) => {
  const total = mode === 'in' ? group.payout : group.stake;

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
        <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
          @{group.username}
        </span>
        <Money
          amount={total}
          currency={currency}
          className="flex-shrink-0 text-sm font-bold text-amber-600 dark:text-amber-400"
        />
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {group.bets.map(bet => (
          <BetRow key={bet.betId} bet={bet} mode={mode} onOpenBet={onOpenBet} t={t} />
        ))}
      </div>
    </div>
  );
};

/**
 * Explains GGR differences caused by sports bets whose stake and payout land in
 * different reporting periods (a June 30 bet that closes on July 2 shows up as
 * unexplained money in July).
 */
const SportsSettlementAuditModal = ({
  agent,
  players,
  rangeStart,
  rangeEnd,
  rangeLabel,
  onClose
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [mode, setMode] = useState('in');

  const playerUsernames = useMemo(
    () => players.map(p => p.username).filter(Boolean),
    [players]
  );

  const runAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProgress(0);

    try {
      const { start, end } = getAuditFetchWindow(rangeStart, rangeEnd);
      const transactions = await fetchSportsTransactions({
        playerIds: playerUsernames,
        start,
        end,
        onProgress: setProgress
      });

      const audit = auditSportsSettlements({
        bets: buildSportsBets(transactions),
        rangeStart,
        rangeEnd
      });

      setResult(audit);
      setActiveCurrency(prev =>
        prev && audit.currencies.includes(prev) ? prev : audit.currencies[0] || null
      );
      setMode(audit.carriedIn.length === 0 && audit.carriedOut.length > 0 ? 'out' : 'in');
    } catch (err) {
      console.error('Sports settlement audit failed:', err);
      setError(err.message || t('settlementAudit.genericError', 'Could not load bet data.'));
    } finally {
      setLoading(false);
    }
  }, [playerUsernames, rangeStart, rangeEnd, t]);

  useEffect(() => {
    runAudit();
  }, [runAudit]);

  const handleOpenBet = useCallback((bet) => {
    navigate(buildBetHistoryLink(bet), {
      state: bet.playerUsername ? { selectedUser: { username: bet.playerUsername } } : undefined
    });
  }, [navigate]);

  const view = useMemo(() => {
    if (!result || !activeCurrency) return null;
    const totals = result.byCurrency[activeCurrency];
    const bets = (mode === 'in' ? result.carriedIn : result.carriedOut)
      .filter(b => b.currency === activeCurrency);
    return {
      totals,
      bets,
      groups: groupBetsByPlayer(bets),
      headline: mode === 'in' ? totals.carriedInPayout : totals.carriedOutStake
    };
  }, [result, activeCurrency, mode]);

  const hasFindings = Boolean(result && result.currencies.length > 0);
  const inCount = result?.carriedIn.length || 0;
  const outCount = result?.carriedOut.length || 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex sm:items-center sm:justify-center sm:p-4">
      <div className="flex flex-col w-full bg-gray-50 dark:bg-gray-900 sm:max-w-2xl sm:max-h-[90vh] sm:rounded-2xl sm:shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {t('settlementAudit.title', 'Late sports payouts')}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              @{agent?.username} · {rangeLabel}
            </p>
          </div>
          <button
            onClick={runAudit}
            disabled={loading}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
            aria-label={t('common.refresh', 'Refresh')}
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t('common.close', 'Close')}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading && (
            <div className="px-6 py-16 text-center space-y-4">
              <ArrowPathIcon className="w-8 h-8 mx-auto animate-spin text-gray-400" />
              <div className="max-w-[200px] mx-auto h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full bg-amber-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settlementAudit.loading', { count: playerUsernames.length }, 'Checking bets...')}
              </p>
            </div>
          )}

          {!loading && error && (
            <div className="p-4">
              <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && !hasFindings && (
            <div className="px-6 py-16 text-center space-y-3">
              <CheckCircleIcon className="w-12 h-12 mx-auto text-emerald-500" />
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('settlementAudit.noFindingsTitle', 'Nothing crosses this period')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto">
                {t('settlementAudit.noFindings', 'Every sports bet opened and closed inside these dates.')}
              </p>
            </div>
          )}

          {!loading && !error && hasFindings && view && (
            <div className="p-3 sm:p-4 space-y-3">
              {result.currencies.length > 1 && (
                <div className="flex gap-1 p-1 rounded-full bg-gray-200 dark:bg-gray-800">
                  {result.currencies.map(currency => (
                    <button
                      key={currency}
                      onClick={() => setActiveCurrency(currency)}
                      className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                        currency === activeCurrency
                          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                          : 'text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {currency}
                    </button>
                  ))}
                </div>
              )}

              {/* Direction switch — the label is the explanation */}
              <div className="grid grid-cols-2 gap-2">
                <ModeCard
                  active={mode === 'in'}
                  onClick={() => setMode('in')}
                  title={t('settlementAudit.tabIn', 'Paid out here')}
                  subtitle={t('settlementAudit.tabInSub', 'bet placed before')}
                  count={inCount}
                  amount={view.totals.carriedInPayout}
                  currency={activeCurrency}
                />
                <ModeCard
                  active={mode === 'out'}
                  onClick={() => setMode('out')}
                  title={t('settlementAudit.tabOut', 'Closes later')}
                  subtitle={t('settlementAudit.tabOutSub', 'bet placed here')}
                  count={outCount}
                  amount={view.totals.carriedOutStake}
                  currency={activeCurrency}
                />
              </div>

              {/* Headline: the number the agent is looking for */}
              <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-4 py-3 text-center">
                <Money
                  amount={view.headline}
                  currency={activeCurrency}
                  className="block text-3xl font-bold text-amber-700 dark:text-amber-300"
                />
                <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                  {mode === 'in'
                    ? t('settlementAudit.headlineIn', 'landed in this period from older bets')
                    : t('settlementAudit.headlineOut', 'staked here, result lands later')}
                </p>
              </div>

              {view.groups.length === 0 ? (
                <p className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('settlementAudit.emptyDirection', 'Nothing here.')}
                </p>
              ) : (
                view.groups.map(group => (
                  <PlayerBlock
                    key={group.username}
                    group={group}
                    currency={activeCurrency}
                    mode={mode}
                    onOpenBet={handleOpenBet}
                    t={t}
                  />
                ))
              )}

              <p className="pt-1 pb-2 text-center text-[11px] text-gray-400 dark:text-gray-500">
                {t('settlementAudit.footnote', 'Tap a bet to open it in Bet History.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ModeCard = ({ active, onClick, title, subtitle, count, amount, currency }) => (
  <button
    onClick={onClick}
    className={`rounded-xl border p-3 text-left transition-colors ${
      active
        ? 'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-amber-900/30'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
    }`}
  >
    <div className="flex items-baseline justify-between gap-1">
      <span className={`text-xs font-semibold ${
        active ? 'text-amber-900 dark:text-amber-200' : 'text-gray-700 dark:text-gray-300'
      }`}>
        {title}
      </span>
      <span className={`text-xs font-bold ${
        active ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400 dark:text-gray-500'
      }`}>
        {count}
      </span>
    </div>
    <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">{subtitle}</div>
    <div className="mt-1 text-sm font-bold text-gray-900 dark:text-gray-100">
      {formatCurrency(amount, currency)}
    </div>
  </button>
);

export default SportsSettlementAuditModal;
